"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Stethoscope, ArrowRight, Activity } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  id: "financial-services",
  title: "Financial Services",
  description: "Modernizing data architectures for private equity firms to drive alpha and operational transparency.",
  iconName: "shield"
}, {
  id: "enterprise-tech",
  title: "Technology & Platforms",
  description: "Re-engineering legacy enterprise systems into AI-native digital ecosystems for global CIOs.",
  iconName: "activity"
}];
export default function ModernHealthcareSimplified() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const renderIcon = (name: string) => {
    switch (name) {
      case "stethoscope":
        return <Stethoscope className="size-6 text-primary" />;
      case "shield":
        return <ShieldCheck className="size-6 text-primary" />;
      default:
        return <Activity className="size-6 text-primary" />;
    }
  };
  return <section data-section-id="146633" id="sectors-we-transform" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-background">
      {}
      <div className="absolute inset-0 z-0">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover object-center opacity-20 grayscale saturate-50" poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/telehealth_platform_interaction_bg.png">
          {}
          <source src="https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4" type="video/mp4" />
        </video>
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/40 to-background"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 py-20">
        <div ref={contentRef} className={`max-w-4xl mx-auto transition-all duration-1000 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="bg-background/90 backdrop-blur-md border border-border/40 rounded-sm p-8 md:p-12 lg:p-16 shadow-2xl shadow-shadow-color/5 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-none bg-primary/5 text-primary text-xs font-mono uppercase tracking-widest mb-8 border border-primary/10">
              <Activity className="size-4" />
              <span>Industry Expertise</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-foreground mb-6 font-sans">
              Industry-Specific <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-chart-1 to-chart-2">
                Intelligence.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed font-medium">
              We partner with private equity and enterprise leaders to re-engineer operations through AI, automation, and high-performance digital systems.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-10 text-left">
              {features.map((feature, i) => <div key={feature.id} data-index={i} className="flex flex-col gap-3 p-6 rounded-sm bg-white border border-border/50 hover:border-primary/30 transition-all duration-300">
                  <div className="size-12 rounded-sm bg-primary/5 flex items-center justify-center mb-2">
                    {renderIcon(feature.iconName)}
                  </div>
                  <h3 className="text-xl font-bold text-foreground tracking-tight">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-sm px-8 h-14 text-sm font-bold uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                <Link to="/IndustriesPage">
                  View Industries
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-sm px-8 h-14 text-sm font-bold uppercase tracking-wider bg-transparent border-border text-foreground hover:bg-secondary transition-colors">
                <Link to="/ServicesPage">
                  Our Services
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}